import {useFormik} from 'formik'
import {lowerCase, startCase, isEmpty, isUndefined} from 'lodash'
import React, {useCallback, useEffect, useState, useRef} from 'react'
import {useIntl} from 'react-intl'
import {Link, useNavigate} from 'react-router-dom'
import {toast} from 'react-toastify'
import {CSSTransition} from 'react-transition-group'
import * as Yup from 'yup'
import FingerprintJS from '@fingerprintjs/fingerprintjs'
import {PasswordMeterComponent} from '../../../../_gori/assets/ts/components'
import {blockPopUp, StorageHelpers} from '../../../../_gori/helpers'
import useCancelToken from '../../../../_gori/hooks/UseCancelToken'
import UseYupValidate from '../../../../_gori/hooks/UseYupValidate'
import {Button, InputTextFormik} from '../../../../_gori/partials/widgets'
import {AcceptTermCheckbox} from '../../../../_gori/partials/widgets/form/AcceptTermCheckbox'
import {InputPassword} from '../../../../_gori/partials/widgets/form/InputPassword'
import AuthService from '../core/_requests'
import {CaptchaConfig} from '../core/_requests'
import ReCAPTCHA from 'react-google-recaptcha'
import {TencentCaptchaWidget} from './TencentCaptchaWidget'
import {ROUTES} from '../../../../_gori/constants'
import {VerificationPhoneModal} from '../Modal/VerificationPhoneModal'
import {SeoHead} from '../../seo'
import {IdentityVerificationModal} from '../Modal/IdentityVerificationModal'

const KEY_RECAPTCHA = process.env.REACT_APP_KEY_RECAPTCHA

interface RegistrationFormProps {
  referrer?: string
  inviteToken?: string | null
}

export function RegistrationForm({referrer, inviteToken}: RegistrationFormProps) {
  const intl = useIntl()
  const navigate = useNavigate()
  const {newCancelToken, isCancel} = useCancelToken()
  const {authYup, infoYup, regexYup} = UseYupValidate()
  const [loading, setLoading] = useState(false)
  const [isValidRecaptcha, setIsValidRecaptcha] = useState<boolean | undefined>(undefined)
  const [tokenRecaptcha, setTokenRecaptcha] = useState(null)
  const [verifiedPhone, setVerifiedPhone] = useState(false)
  const [errorMessageVerifiedPhone, setErrorMessageVerifiedPhone] = useState(false)
  const recaptchaRef = useRef(null)
  const [recaptchaError, setRecaptchaError] = useState({required: false, error: false})
  const [captchaConfig, setCaptchaConfig] = useState<CaptchaConfig | null>(null)
  const [captchaConfigLoading, setCaptchaConfigLoading] = useState<boolean>(true)
  const [captchaConfigError, setCaptchaConfigError] = useState<boolean>(false)
  const [tencentTicket, setTencentTicket] = useState<string | null>(null)
  const [tencentRandstr, setTencentRandstr] = useState<string | null>(null)
  const tencentReloadRef = useRef<(() => void) | null>(null)
  const [verifiedBaseInformation, setVerifiedBaseInformation] = useState(false)
  const [formToken, setFormToken] = useState<string>('')
  const [deviceFingerprint, setDeviceFingerprint] = useState<string>('')
  const [fpReady, setFpReady] = useState(false)
  const [fpError, setFpError] = useState(false)
  const [fpLoading, setFpLoading] = useState(false)
  const mountedRef = useRef(true)
  const [showModal, setShowModal] = useState<{
    verify_phone: boolean
    identity_verification: boolean
  }>({
    verify_phone: false,
    identity_verification: false,
  })
  const [dataModal, setDataModal] = useState<{
    verify_phone: any
    identity_verification: any
  }>({
    verify_phone: [],
    identity_verification: [],
  })

  const registrationSchema = Yup.object().shape({
    company_name: authYup.companyName(50),
    first_name: regexYup.inputText('FIRST_NAME', true, 50),
    last_name: regexYup.inputText('LAST_NAME', true, 50),
    email: infoYup.email(),
    password: authYup.password(),
    password_confirmation: authYup.passwordConfirmation(),
    accept_terms: authYup.acceptTerms,
    receive_promotional_emails: authYup.receivePromotionalEmails,
  })

  const initialValues = {
    company_name: '',
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    password: '',
    password_confirmation: '',
    accept_terms: false,
    receive_promotional_emails: false,
    business_address: '',
    form_token: '',
  }

  const handleSubmitError = (errorsMessage: any) => {
    if (isEmpty(errorsMessage) || isUndefined(errorsMessage)) {
      return
    }

    Object.entries(errorsMessage).forEach(([key, value]: [any, any]) => {
      for (let i = 0; i < value.length; i++) {
        const errMessage = value[i].split('|')

        formik.errors[key] = formik.errors[key]
          ? formik.errors[key] +
            ', ' +
            intl.formatMessage({id: errMessage[1]}, {input: startCase(lowerCase(errMessage[0]))})
          : intl.formatMessage({id: errMessage[1]}, {input: startCase(lowerCase(errMessage[0]))})
      }
    })
  }

  const formik = useFormik({
    initialValues,
    validationSchema: registrationSchema,
    onSubmit: async (values, {setSubmitting}) => {
      if (!verifiedBaseInformation || values.business_address) {
        setSubmitting(false)
        return
      }
      if (!verifiedPhone) {
        setErrorMessageVerifiedPhone(true)
        return
      }
      if (captchaConfig?.provider === 'google') {
        // @ts-ignore
        if (recaptchaRef.current.getValue() === null || recaptchaRef.current.getValue() === '') {
          setRecaptchaError({required: true, error: false})
          setIsValidRecaptcha(false)
          return
        }
      }

      if (captchaConfig?.provider === 'tencent' && !tencentTicket) {
        setIsValidRecaptcha(false)
        return
      }

      try {
        setLoading(true)
        const payload: any = {
          company_name: values.company_name,
          first_name: values.first_name,
          last_name: values.last_name,
          phone: values.phone,
          email: values.email,
          password: values.password,
          password_confirmation: values.password_confirmation,
          accept_terms: values.accept_terms,
          receive_promotional_emails: values.receive_promotional_emails,
          business_address: values.business_address,
          form_token: formToken,
          phone_verified: verifiedPhone,
          device_fingerprint: deviceFingerprint,
          captcha_provider: captchaConfig?.provider ?? 'google',
          ...(captchaConfig?.provider === 'google' && tokenRecaptcha
            ? {token_recaptcha: tokenRecaptcha}
            : {}),
          ...(captchaConfig?.provider === 'tencent' && tencentTicket
            ? {token_recaptcha: tencentTicket, randstr: tencentRandstr}
            : {}),
          ...(referrer ? {referrer} : {}),
          ...(inviteToken ? {token: inviteToken} : {}),
        }

        if (inviteToken) {
          await AuthService.activeInviteToken(payload, {cancelToken: newCancelToken()})
        } else {
          const response = await AuthService.register(payload, {cancelToken: newCancelToken()})
          if (response.next_step) {
            return
          }
        }

        if ((window as any).posthog?.identify) {
          ;(window as any).posthog.identify(values.email)
        }
        ;(window as any).dataLayer = (window as any).dataLayer || []
        ;(window as any).dataLayer.push({
          event: 'Account_Creation',
          method: 'email',
        })
        StorageHelpers.setItemLocalStorage('REGISTERED_EMAIL', values.email)
        navigate(ROUTES.AUTH.PENDING_ACTIVATION)
      } catch (error: any) {
        if (isCancel(error)) return

        // Close the phone verification modal if it's still open when registration fails
        setShowModal((prev) => ({...prev, verify_phone: false, identity_verification: false}))

        if (captchaConfig?.provider === 'google') {
          if (error?.response?.data?.error?.fields?.invalid_recaptcha) {
            setRecaptchaError({required: false, error: true})
          }
          setIsValidRecaptcha(false)
          // @ts-ignore
          recaptchaRef.current.reset()
        }

        // Reset Tencent CAPTCHA after any failure — ticket is single-use and cannot be resubmitted
        if (captchaConfig?.provider === 'tencent') {
          setTencentTicket(null)
          setTencentRandstr(null)
          setIsValidRecaptcha(false)
          tencentReloadRef.current?.()
        }

        const fields = error?.response?.data?.error?.fields
        const message = error?.response?.data?.error?.message

        toast.dismiss('registration-error')

        if (fields && Object.keys(fields).length > 0) {
          handleSubmitError(fields)
          // Also show a toast so the user sees feedback after the modal closes
          toast.error(message || intl.formatMessage({id: 'REGISTRATION_BLOCKED'}), {
            toastId: 'registration-error',
          })
        } else {
          toast.error(message || intl.formatMessage({id: 'REGISTRATION_BLOCKED'}), {
            toastId: 'registration-error',
          })
        }
      } finally {
        setLoading(false)
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    const baseDomain = process.env.REACT_APP_DOMAIN
    if (document.location.host !== baseDomain) {
      const protocol = document.location.protocol
      document.location.replace(protocol + '//' + baseDomain + '/')
    }
    PasswordMeterComponent.bootstrap()
  }, [])

  useEffect(() => {
    return () => {
      mountedRef.current = false
    }
  }, [])

  const loadFingerprint = useCallback(() => {
    setFpError(false)
    setFpLoading(true)

    const timeout = setTimeout(() => {
      if (!mountedRef.current) return
      setFpLoading(false)
      setFpError(true)
    }, 10000)

    FingerprintJS.load()
      .then((fp) => fp.get())
      .then((result) => {
        if (!mountedRef.current) return
        setDeviceFingerprint(result.visitorId)
        setFpReady(true)
        setFpLoading(false)
        clearTimeout(timeout)
      })
      .catch(() => {
        if (!mountedRef.current) return
        setFpLoading(false)
        setFpError(true)
        clearTimeout(timeout)
      })

    return () => clearTimeout(timeout)
  }, [])

  useEffect(() => {
    loadFingerprint()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getVersion = useCallback(async () => {
    try {
      await AuthService.getVersion()
    } catch (error) {
      console.log(error)
    }
  }, [])

  useEffect(() => {
    getVersion()
  }, [getVersion])

  const getConfigCaptcha = useCallback(async () => {
    setCaptchaConfigLoading(true)
    setCaptchaConfigError(false)
    try {
      const response = await AuthService.getConfigCaptcha()
      setCaptchaConfig(response.data)
      setFormToken(response.data.anti_bot?.form_token ?? '')
      // Disable submit immediately for Tencent until the widget fires onVerify
      if (response.data.provider === 'tencent') {
        setIsValidRecaptcha(false)
      }
    } catch (error) {
      setCaptchaConfigError(true)
    } finally {
      setCaptchaConfigLoading(false)
    }
  }, [])

  useEffect(() => {
    getConfigCaptcha()
  }, [getConfigCaptcha])

  const checkChangePhone = (value) => {
    const phoneVerified = StorageHelpers.getItemLocalStorage('PHONE_VERIFIED')
    if (isEmpty(phoneVerified)) {
      return
    }

    if (phoneVerified !== value) {
      setVerifiedPhone(false)
    } else {
      setVerifiedPhone(true)
    }
  }

  const handleRecaptchaChange = (token: React.SetStateAction<null>) => {
    if (token !== null) {
      setRecaptchaError({required: false, error: false})
      setTokenRecaptcha(token)
      setIsValidRecaptcha(true)
    }
  }

  const handleError = () => {
    // @ts-ignore
    recaptchaRef.current.reset()
    setIsValidRecaptcha(false)
  }

  const handleExpired = () => {
    setIsValidRecaptcha(false)
    // @ts-ignore
    recaptchaRef.current.reset()
  }

  const validateAndOpenVerifyModal = async () => {
    const errors = await formik.validateForm()

    if (Object.keys(errors).length === 0) {
      try {
        setLoading(true)
        const payload: any = {
          company_name: formik.values.company_name,
          first_name: formik.values.first_name,
          last_name: formik.values.last_name,
          email: formik.values.email,
          password: formik.values.password,
          password_confirmation: formik.values.password_confirmation,
          accept_terms: formik.values.accept_terms,
          receive_promotional_emails: formik.values.receive_promotional_emails,
          business_address: formik.values.business_address,
          form_token: formToken,
          device_fingerprint: deviceFingerprint,
          captcha_provider: captchaConfig?.provider ?? 'google',
          ...(captchaConfig?.provider === 'google' && tokenRecaptcha
            ? {token_recaptcha: tokenRecaptcha}
            : {}),
          ...(captchaConfig?.provider === 'tencent' && tencentTicket
            ? {token_recaptcha: tencentTicket, randstr: tencentRandstr}
            : {}),
          ...(referrer ? {referrer} : {}),
          ...(inviteToken ? {token: inviteToken} : {}),
        }
        const response = await AuthService.verify(payload, {cancelToken: newCancelToken()})
        if (response.next_step) {
          return
        }

        setVerifiedBaseInformation(true)
        setShowModal((prev) => ({...prev, identity_verification: true}))
      } catch (error: any) {
        handleSubmitError(error?.response?.data?.error?.fields)
        setVerifiedBaseInformation(false)
      }
      setLoading(false)
    } else {
      formik.setTouched(Object.keys(errors).reduce((acc, key) => ({...acc, [key]: true}), {}))
    }
  }

  return (
    <>
      <SeoHead />
      {showModal.verify_phone && (
        <VerificationPhoneModal
          show={showModal.verify_phone}
          data={dataModal.verify_phone}
          handleClose={() => {
            setShowModal((prev) => ({...prev, verify_phone: false}))
          }}
          phone={formik.values.phone}
          fromPage='registration'
          verifiedPhone={verifiedPhone}
          updateVerifiedPhone={() => {
            setVerifiedPhone(true)
            setErrorMessageVerifiedPhone(false)
          }}
          triggerSubmitForm={formik.submitForm}
        />
      )}
      {showModal.identity_verification && (
        <IdentityVerificationModal
          formik={formik}
          show={showModal.identity_verification}
          handleClose={() => {
            setShowModal((prev) => ({...prev, identity_verification: false}))
          }}
          checkChangePhone={checkChangePhone}
          setVerifiedPhone={setVerifiedPhone}
          errorMessageVerifiedPhone={errorMessageVerifiedPhone}
          setErrorMessageVerifiedPhone={setErrorMessageVerifiedPhone}
          setShowModal={setShowModal}
          setDataModal={setDataModal}
        />
      )}
      <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
        <div className='w-lg-600px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto'>
          <div className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'>
            <div className='mb-10 text-center'>
              <h1 className='text-dark mb-3'>{intl.formatMessage({id: 'CREATE_AN_ACCOUNT'})}</h1>
              <div className='text-gray-400 fw-bold fs-4'>
                {intl.formatMessage({id: 'ALREADY_HAVE_AN_ACCOUNT'})}{' '}
                <Link
                  to={ROUTES.AUTH.LOGIN}
                  className='text-primary-deep text-hover-primary fw-bolder'
                >
                  {intl.formatMessage({id: 'SIGN_IN_HERE'})}
                </Link>
              </div>
            </div>

            {/* Additional data */}
            <div className='fv-row mb-7 form-field-optional position-absolute start-0 top-0 overflow-hidden visually-hidden'>
              <InputTextFormik
                labelClassName='form-label fw-bolder text-dark fs-6'
                size='lg'
                formik={formik}
                name='business_address'
                type='text'
                autoComplete='business_address'
              />
            </div>
            {/* End additional data */}

            <div className='fv-row mb-7'>
              <InputTextFormik
                labelClassName='form-label fw-bolder text-dark fs-6'
                label={intl.formatMessage({id: 'COMPANY_NAME'})}
                size='lg'
                formik={formik}
                name='company_name'
                required
              />
            </div>
            <div className='row fv-row mb-9 fv-plugins-icon-container'>
              <div className='col-xl-6'>
                <InputTextFormik
                  labelClassName='form-label fw-bolder text-dark fs-6'
                  label={intl.formatMessage({id: 'FIRST_NAME'})}
                  size='lg'
                  formik={formik}
                  name='first_name'
                  required
                />
              </div>
              <div className='col-xl-6'>
                <InputTextFormik
                  labelClassName='form-label fw-bolder text-dark fs-6'
                  label={intl.formatMessage({id: 'LAST_NAME'})}
                  size='lg'
                  formik={formik}
                  name='last_name'
                  required
                />
              </div>
            </div>
            <div className='fv-row mb-7 fv-plugins-icon-container'>
              <InputTextFormik
                labelClassName='form-label fw-bolder text-dark fs-6'
                label={intl.formatMessage({id: 'EMAIL'})}
                size='lg'
                formik={formik}
                name='email'
                required
              />
            </div>
            <InputPassword
              className={'mb-10'}
              labelClassName='form-label fw-bolder text-dark fs-6'
              label={intl.formatMessage({id: 'PASSWORD'})}
              size={'lg'}
              strengthMessage={intl.formatMessage({id: 'PASSWORD_STRENGTH_MESSAGE'})}
              formik={formik}
              name={'password'}
              required
            />
            <InputPassword
              className={'mb-5'}
              labelClassName='form-label fw-bolder text-dark fs-6'
              label={intl.formatMessage({id: 'CONFIRM_PASSWORD'})}
              size='lg'
              openHighlight={false}
              formik={formik}
              name={'password_confirmation'}
              required
            />

            <>
              <AcceptTermCheckbox
                alignTop={true}
                className={'mb-5'}
                formik={formik}
                name={'accept_terms'}
                splitLabelCheck={[
                  intl.formatMessage({id: 'AGREE_MESSAGE_PRE_PHONE_VERIFY_1'}),
                  intl.formatMessage({id: 'AGREE_MESSAGE_PRE_PHONE_VERIFY_2'}),
                ]}
                links={[
                  {
                    label: intl.formatMessage({id: 'TERMS_OF_SERVICE'}),
                    onClick: () => {
                      if (blockPopUp('https://shipbae.com/service-terms')) {
                        toast.warning(intl.formatMessage({id: 'POP_UP_IS_BLOCKED'}))
                      }
                    },
                  },
                  {
                    label: intl.formatMessage({id: 'PRIVACY_POLICY'}),
                    onClick: () => {
                      if (blockPopUp('https://shipbae.com/privacy-policy')) {
                        toast.warning(intl.formatMessage({id: 'POP_UP_IS_BLOCKED'}))
                      }
                    },
                  },
                  {
                    label: intl.formatMessage({
                      id: 'USPS_LABEL_INTEGRITY_AND_COMPLIANCE_AGREEMENT',
                    }),
                    onClick: () => {
                      if (blockPopUp('https://shipbae.com/agreement')) {
                        toast.warning(intl.formatMessage({id: 'POP_UP_IS_BLOCKED'}))
                      }
                    },
                  },
                ]}
              />
              <div
                className={`fv-row fv-plugins-icon-container fv-plugins-bootstrap5-row-invalid mb-10`}
              >
                <div
                  className={`form-check form-check-custom form-check-solid form-check-inline align-items-start`}
                >
                  <input
                    type='checkbox'
                    className={`form-check-input shadow-none mt-1`}
                    {...formik.getFieldProps('receive_promotional_emails')}
                  />
                  <span className={`form-check-label fw-bold text-gray-700 fs-7`}>
                    {intl.formatMessage({id: 'AGREE_TO_RECEIVE_EMAILS'})}
                  </span>
                </div>
              </div>
            </>
            {captchaConfigError && (
              <p className='d-flex justify-content-center align-items-center text-danger'>
                {intl.formatMessage({id: 'CAPTCHA_CONFIG_ERROR'})}{' '}
                <span
                  className='text-primary cursor-pointer ms-2 text-decoration-underline'
                  onClick={getConfigCaptcha}
                >
                  {intl.formatMessage({id: 'RETRY'})}
                </span>
              </p>
            )}

            {captchaConfig?.provider === 'google' && KEY_RECAPTCHA && (
              <div className='d-flex justify-content-center mb-5'>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  onChange={handleRecaptchaChange}
                  onExpired={handleExpired}
                  onErrored={handleError}
                  sitekey={KEY_RECAPTCHA}
                  hl='en'
                  isolated={true}
                />
              </div>
            )}

            {captchaConfig?.provider === 'google' && recaptchaError.error && (
              <p className='d-flex justify-content-center text-danger'>
                {intl.formatMessage({id: 'RECAPTCHA_ERROR_MESSAGE'})}
              </p>
            )}

            {captchaConfig?.provider === 'google' && recaptchaError.required && (
              <p className='d-flex justify-content-center text-danger'>
                {intl.formatMessage({id: 'RECAPTCHA_ERROR_REQUIRED'})}
              </p>
            )}

            {captchaConfig?.provider === 'tencent' && captchaConfig.tencent_app_id && (
              <TencentCaptchaWidget
                appId={captchaConfig.tencent_app_id}
                onReady={(reload) => {
                  tencentReloadRef.current = reload
                }}
                onVerify={(ticket, randstr) => {
                  setTencentTicket(ticket)
                  setTencentRandstr(randstr)
                  setIsValidRecaptcha(true)
                }}
              />
            )}

            {fpError && (
              <p className='d-flex justify-content-center align-items-center text-danger'>
                {intl.formatMessage({id: 'DEVICE_FINGERPRINT_REQUIRED'})}{' '}
                <span
                  className='text-primary cursor-pointer ms-2 text-decoration-underline'
                  onClick={loadFingerprint}
                >
                  {intl.formatMessage({id: 'RETRY'})}
                </span>
              </p>
            )}

            <div className='text-center'>
              <Button
                className='btn btn-lg btn-advanced-primary'
                label={intl.formatMessage({id: 'SUBMIT'})}
                loadingText={intl.formatMessage({id: 'SUBMIT'})}
                loading={loading}
                disabled={
                  formik.isSubmitting ||
                  !formik.isValid ||
                  fpError ||
                  fpLoading ||
                  !fpReady ||
                  captchaConfigLoading ||
                  captchaConfigError ||
                  (isUndefined(isValidRecaptcha) ? undefined : !isValidRecaptcha)
                }
                event={validateAndOpenVerifyModal}
              />
            </div>
          </div>
        </div>
      </CSSTransition>
    </>
  )
}
